export type LegacyIcon = string | google.maps.Icon | google.maps.Symbol | undefined;
export type AdvancedMarkerOptions = google.maps.marker.AdvancedMarkerElementOptions & {
  icon?: LegacyIcon;
  visible?: boolean;
};

const isSymbolIcon = (icon: LegacyIcon): icon is google.maps.Symbol =>
  typeof icon === 'object' && icon !== null && 'path' in icon;

const isImageIcon = (icon: LegacyIcon): icon is google.maps.Icon =>
  typeof icon === 'object' && icon !== null && 'url' in icon;

// Advanced markers render DOM nodes instead of image sprites, so let's build small wrapper
// elements that mimic the old icon offsets/anchor points
const createWrapper = (transform: string) => {
  const wrapper = document.createElement('div');
  wrapper.style.position = 'absolute';
  wrapper.style.transform = transform;
  wrapper.style.display = 'flex';
  wrapper.style.alignItems = 'center';
  wrapper.style.justifyContent = 'center';

  return wrapper;
};

const createCircleElement = (icon: google.maps.Symbol) => {
  // Only support CIRCLE path for now
  if (icon.path !== google.maps.SymbolPath.CIRCLE) {
    return undefined;
  }

  const diameter = (icon.scale ?? 4) * 2;
  const wrapper = createWrapper('translate(-50%, -50%)');

  wrapper.style.width = `${diameter}px`;
  wrapper.style.height = `${diameter}px`;
  wrapper.style.borderRadius = '50%';
  wrapper.style.boxSizing = 'border-box';
  wrapper.style.backgroundColor = icon.fillColor ?? '#000000';
  wrapper.style.opacity = `${icon.fillOpacity ?? 1}`;
  wrapper.style.border = `${icon.strokeWeight ?? 1}px solid ${icon.strokeColor ?? '#000000'}`;
  wrapper.style.boxShadow = '0 0 4px rgba(0,0,0,0.25)';

  return wrapper;
};

const createImageElement = (icon: LegacyIcon) => {
  const url = (() => {
    if (typeof icon === 'string') {
      return icon;
    }

    if (isImageIcon(icon)) {
      return icon.url;
    }

    return undefined;
  })();

  if (!url) {
    return undefined;
  }

  const wrapper = createWrapper('translate(-50%, -100%)');
  if (!wrapper) {
    return undefined;
  }

  const img = document.createElement('img');
  img.src = url;
  img.alt = '';
  img.style.display = 'block';
  img.style.pointerEvents = 'none';
  img.draggable = false;

  const scaledSize = isImageIcon(icon) ? icon.scaledSize || icon.size : undefined;

  if (scaledSize) {
    img.style.width = `${scaledSize.width}px`;
    img.style.height = `${scaledSize.height}px`;
  }

  wrapper.appendChild(img);
  return wrapper;
};

export const createMarkerContentFromIcon = (icon?: LegacyIcon) => {
  // Marker props still pass the legacy `icon` shape; convert it to DOM content lazily.
  if (!icon || typeof document === 'undefined') {
    return undefined;
  }

  if (isSymbolIcon(icon)) {
    return createCircleElement(icon);
  }

  return createImageElement(icon);
};

const toLatLngLiteral = (
  position?: google.maps.LatLng | google.maps.LatLngLiteral | google.maps.LatLngAltitudeLiteral | null,
) => {
  if (!position) {
    return undefined;
  }

  if (typeof (position as google.maps.LatLngLiteral).lat === 'number') {
    return position as google.maps.LatLngLiteral;
  }

  const latLng = position as google.maps.LatLng;
  return {
    lat: latLng.lat(),
    lng: latLng.lng(),
  };
};

export const applyAdvancedMarkerOptions = (
  marker: google.maps.marker.AdvancedMarkerElement,
  options: AdvancedMarkerOptions,
) => {
  // Keep calling code compatible with google.maps.Marker by normalizing the bits that
  // AdvancedMarkerElement understands (position, zIndex, DOM content, clickability).
  const targetMarker = marker;
  const normalizedPosition = toLatLngLiteral(options.position);
  if (normalizedPosition) {
    targetMarker.position = normalizedPosition;
  }

  if (typeof options.zIndex !== 'undefined') {
    targetMarker.zIndex = options.zIndex;
  }

  if (typeof options.title !== 'undefined') {
    targetMarker.title = options.title ?? '';
  }

  if (typeof options.visible !== 'undefined') {
    targetMarker.element.style.display = options.visible ? '' : 'none';
  }

  const content = createMarkerContentFromIcon(options.icon);
  if (content) {
    targetMarker.content = content;
  } else if (!options.icon) {
    targetMarker.content = null;
  }
};

export const createAdvancedMarkerElement = ({
  map,
  options,
  gmpClickable = true,
}: {
  map: google.maps.Map | null;
  options?: AdvancedMarkerOptions;
  gmpClickable?: boolean;
}) => {
  // Shared factory so we only duplicate the DOM - legacy option glue in one place.
  // gmpClickable enables keyboard navigation and proper ARIA attributes for accessibility
  const marker = new google.maps.marker.AdvancedMarkerElement({
    map: map ?? undefined,
    gmpClickable,
  });

  if (options) {
    applyAdvancedMarkerOptions(marker, options);
  }

  return marker;
};
